import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import { EXHIBITION_IMAGES } from "@/data/content";
import { useT, useLang } from "@/i18n";

function Section({ heading, children }) {
  return (
    <div className="space-y-4">
      <h3 className="text-xl lg:text-2xl font-bold uppercase tracking-tight text-[#C20E1A]">
        {heading}
      </h3>
      <div className="space-y-4 text-base leading-relaxed text-black">{children}</div>
    </div>
  );
}

function PrivacyEN() {
  return (
    <div className="space-y-14">
      <div className="space-y-4 text-base leading-relaxed text-black">
        <p>
          Unless stated otherwise below, the provision of your personal data is neither legally nor
          contractually obligatory, nor required for conclusion of a contract. You are not obliged
          to provide your data. Not providing it will have no consequences. This only applies as
          long as the processing procedures below do not state otherwise.
        </p>
        <p>&ldquo;Personal data&rdquo; is any information relating to an identified or identifiable natural person.</p>
      </div>
      <Section heading="Server log files">
        <p>You can use our websites without submitting personal data.</p>
        <p>
          Every time our website is accessed, user data is transferred to us or our web hosts/IT
          service providers by your internet browser and stored in server log files. This stored
          data includes for example the name of the site called up, date and time of the request,
          the IP address, amount of data transferred and the provider making the request. The
          processing is carried out on the basis of Article 6(1) f) GDPR due to our legitimate
          interests in ensuring the smooth operation of our website as well as improving our
          services.
        </p>
      </Section>
      <Section heading="Contact">
        <p className="font-bold text-black">Responsible person</p>
        <p>
          Contact us at any time. The person responsible for data processing is: Artful Events
          Europe GmbH, Anton-Kux-Str. 2, 41460 Neuss Deutschland, +49 2131 3134985,{" "}
          <a href="mailto:info@artful-events.com" className="link-red">info@artful-events.com</a>
        </p>
      </Section>
      <Section heading="Data processing — TCO Regulation & Digital Services Act">
        <p>
          If you send us information regarding suspected terrorist content or other illegal content
          by email, contact form or other electronic means of communication provided by us, we will
          only collect your personal data (email address, name, message text) to the extent
          provided by you. The data processing serves the purpose of carrying out a reporting
          procedure in accordance with the aforementioned regulations. The data processing is
          carried out to fulfill a legal obligation on the basis of Art. 6 para. 1 lit. c GDPR in
          conjunction with Art. 15 TCO-VO and Art. 6 para. 1 lit. c GDPR in conjunction with Art.
          11, 12 and 16 of the Digital Services Act.
        </p>
        <p>Your data will then be deleted in compliance with statutory retention periods.</p>
      </Section>
      <Section heading="Proactive contact of the customer by e-mail">
        <p>
          If you make contact with us proactively via email, we shall collect your personal data
          (name, email address, message text) only to the extent provided by you. The purpose of
          the data processing is to handle and respond to your contact request.
        </p>
        <p>
          If the initial contact serves to implement pre-contractual measures (e.g. consultation in
          the case of purchase interest, order creation) or concerns an agreement already concluded
          between you and us, this data processing takes place on the basis of Article 6(1)(b)
          GDPR. If the initial contact occurs for other reasons, this data processing takes place
          on the basis of Article 6(1)(f) GDPR for the purposes of our overriding, legitimate
          interest in handling and responding to your request. In this case, on grounds relating to
          your particular situation, you have the right to object at any time to this processing of
          personal data concerning you and carried out on the basis of Article 6(1)(f) GDPR.
        </p>
        <p>
          We will only use your email address to process your request. Your data will subsequently
          be deleted in compliance with statutory retention periods, unless you have agreed to
          further processing and use.
        </p>
      </Section>
      <Section heading="Duration of Storage">
        <p>
          The data will be stored under consideration of legal retention periods and then deleted
          after expiration of the period, unless you have not agreed to further processing and use.
        </p>
      </Section>
      <Section heading="Rights of the affected person">
        <p>
          If the legal requirements are fulfilled, you have the following rights according to art.
          15 to 20 GDPR: Right to information, correction, deletion, restriction of processing,
          data portability. You also have a right of objection against processing based on art. 6
          (1) GDPR, and to processing for the purposes of direct marketing, according to art. 21
          (1) GDPR.
        </p>
      </Section>
      <Section heading="Right to complain to the regulatory authority">
        <p>
          You have the right to complain to the regulatory authority according to art. 77 GDPR if
          you believe that your data is not being processed legally.
        </p>
      </Section>
      <Section heading="Right to object">
        <p>
          If the data processing outlined here is based on our legitimate interests in accordance
          with Article 6(1)f) GDPR, you have the right for reasons arising from your particular
          situation to object at any time to the processing of your data with future effect. If the
          objection is successful, we will no longer process the personal data, unless we can
          demonstrate compelling legitimate grounds for the processing that outweigh your interests
          or rights and freedoms, or the processing is intended for the assertion, exercise or
          defence of legal claims.
        </p>
      </Section>
    </div>
  );
}

function PrivacyDE() {
  return (
    <div className="space-y-14">
      <div className="space-y-4 text-base leading-relaxed text-black">
        <p>
          Soweit nachstehend keine anderen Angaben gemacht werden, ist die Bereitstellung Ihrer
          personenbezogenen Daten weder gesetzlich oder vertraglich vorgeschrieben, noch für einen
          Vertragsabschluss erforderlich. Sie sind zur Bereitstellung der Daten nicht verpflichtet.
          Eine Nichtbereitstellung hat keine Folgen. Dies gilt nur soweit bei den nachfolgenden
          Verarbeitungsvorgängen keine anderweitige Angabe gemacht wird.
        </p>
        <p>
          &bdquo;Personenbezogene Daten&ldquo; sind alle Informationen, die sich auf eine
          identifizierte oder identifizierbare natürliche Person beziehen.
        </p>
      </div>
      <Section heading="Server-Logfiles">
        <p>Sie können unsere Webseiten besuchen, ohne Angaben zu Ihrer Person zu machen.</p>
        <p>
          Bei jedem Zugriff auf unsere Website werden an uns oder unseren Webhoster /
          IT-Dienstleister Nutzungsdaten durch Ihren Internet Browser übermittelt und in
          Protokolldaten (sog. Server-Logfiles) gespeichert. Zu diesen gespeicherten Daten gehören
          z. B. der Name der aufgerufenen Seite, Datum und Uhrzeit des Abrufs, die IP-Adresse, die
          übertragene Datenmenge und der anfragende Provider. Die Verarbeitung erfolgt auf
          Grundlage des Art. 6 Abs. 1 lit. f DSGVO aus unserem überwiegenden berechtigten Interesse
          an der Gewährleistung eines störungsfreien Betriebs unserer Website sowie zur
          Verbesserung unseres Angebotes.
        </p>
      </Section>
      <Section heading="Kontakt">
        <p className="font-bold text-black">Verantwortlicher</p>
        <p>
          Kontaktieren Sie uns auf Wunsch. Verantwortlicher für die Datenverarbeitung ist: AvantgART
          Concept Management GmbH, Anton-Kux-Str. 2, 41460 Neuss Deutschland, +49 2131 3134985,{" "}
          <a href="mailto:info@artful-events.com" className="link-red">info@artful-events.com</a>
        </p>
      </Section>
      <Section heading="Datenverarbeitung bei Nutzung des Meldesystems nach TCO-Verordnung und Digital Services Act">
        <p>
          Sofern Sie uns per E-Mail, per Kontaktformular oder über sonstige von uns zur Verfügung
          gestellte elektronische Kommunikationsmittel Informationen hinsichtlich mutmaßlicher
          terroristischer Inhalte oder sonstiger rechtswidriger Inhalte übermitteln, erheben wir
          Ihre personenbezogenen Daten (E-Mail-Adresse, Name, Nachrichtentext) nur in dem von Ihnen
          zur Verfügung gestellten Umfang. Die Datenverarbeitung dient dem Zweck der Durchführung
          eines den genannten Verordnungen entsprechenden Meldeverfahrens.
        </p>
        <p>
          Die Datenverarbeitung erfolgt zur Erfüllung einer rechtlichen Verpflichtung auf Grundlage
          des Art. 6 Abs. 1 lit. c DSGVO iVm Art. 15 TCO-VO sowie Art. 6 Abs. 1 lit. c DSGVO iVm
          Art. 11, 12 und 16 des Digital Services Act.
        </p>
        <p>Ihre Daten werden anschließend unter Beachtung gesetzlicher Aufbewahrungsfristen gelöscht.</p>
      </Section>
      <Section heading="Initiativ-Kontaktaufnahme des Kunden per E-Mail">
        <p>
          Wenn Sie per E-Mail initiativ mit uns in Geschäftskontakt treten, erheben wir Ihre
          personenbezogenen Daten (Name, E-Mail-Adresse, Nachrichtentext) nur in dem von Ihnen zur
          Verfügung gestellten Umfang. Die Datenverarbeitung dient der Bearbeitung und Beantwortung
          Ihrer Kontaktanfrage.
        </p>
        <p>
          Wenn die Kontaktaufnahme der Durchführung vorvertraglichen Maßnahmen (bspw. Beratung bei
          Kaufinteresse, Angebotserstellung) dient oder einen bereits zwischen Ihnen und uns
          geschlossenen Vertrag betrifft, erfolgt diese Datenverarbeitung auf Grundlage des Art. 6
          Abs. 1 lit. b DSGVO. Erfolgt die Kontaktaufnahme aus anderen Gründen, erfolgt diese
          Datenverarbeitung auf Grundlage des Art. 6 Abs. 1 lit. f DSGVO aus unserem überwiegenden
          berechtigten Interesse an der Bearbeitung und Beantwortung Ihrer Anfrage. In diesem Fall
          haben Sie das Recht, aus Gründen, die sich aus Ihrer besonderen Situation ergeben,
          jederzeit dieser auf Art. 6 Abs. 1 lit. f DSGVO beruhenden Verarbeitungen Sie betreffender
          personenbezogener Daten zu widersprechen.
        </p>
        <p>
          Ihre E-Mail-Adresse nutzen wir nur zur Bearbeitung Ihrer Anfrage. Ihre Daten werden
          anschließend unter Beachtung gesetzlicher Aufbewahrungsfristen gelöscht, sofern Sie der
          weitergehenden Verarbeitung und Nutzung nicht zugestimmt haben.
        </p>
      </Section>
      <Section heading="Dauer der Speicherung">
        <p>
          Die Daten werden unter Berücksichtigung gesetzlicher Aufbewahrungsfristen gespeichert und
          dann nach Fristablauf gelöscht, sofern Sie der weitergehenden Verarbeitung und Nutzung
          nicht zugestimmt haben.
        </p>
      </Section>
      <Section heading="Rechte der betroffenen Person">
        <p>
          Ihnen stehen bei Vorliegen der gesetzlichen Voraussetzungen folgende Rechte nach Art. 15
          bis 20 DSGVO zu: Recht auf Auskunft, auf Berichtigung, auf Löschung, auf Einschränkung
          der Verarbeitung, auf Datenübertragbarkeit.
        </p>
        <p>
          Außerdem steht Ihnen nach Art. 21 Abs. 1 DSGVO ein Widerspruchsrecht gegen die
          Verarbeitungen zu, die auf Art. 6 Abs. 1 f DSGVO beruhen, sowie gegen die Verarbeitung
          zum Zwecke von Direktwerbung.
        </p>
      </Section>
      <Section heading="Beschwerderecht bei der Aufsichtsbehörde">
        <p>
          Sie haben gemäß Art. 77 DSGVO das Recht, sich bei der Aufsichtsbehörde zu beschweren,
          wenn Sie der Ansicht sind, dass die Verarbeitung Ihrer personenbezogenen Daten nicht
          rechtmäßig erfolgt.
        </p>
      </Section>
      <Section heading="Widerspruchsrecht">
        <p>
          Beruhen die hier aufgeführten personenbezogenen Datenverarbeitungen auf Grundlage unseres
          berechtigten Interesses nach Art. 6 Abs. 1 lit. f DSGVO, haben Sie das Recht aus Gründen,
          die sich aus Ihrer besonderen Situation ergeben, jederzeit diesen Verarbeitungen mit
          Wirkung für die Zukunft zu widersprechen.
        </p>
        <p>
          Nach erfolgtem Widerspruch wird die Verarbeitung der betroffenen Daten beendet, es sei
          denn, wir können zwingende schutzwürdige Gründe für die Verarbeitung nachweisen, die
          Ihren Interessen, Rechten und Freiheiten überwiegen, oder wenn die Verarbeitung der
          Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen dient.
        </p>
      </Section>
    </div>
  );
}

export default function PrivacyPolicy() {
  const t = useT();
  const { lang } = useLang();
  const isDe = lang === "de";
  return (
    <div data-testid="privacy-page" className="bg-white">
      <PageHero
        eyebrow={t.pages.privacy.eyebrow}
        title={t.pages.privacy.title}
        subtitle={t.pages.privacy.subtitle}
        image={EXHIBITION_IMAGES[1]}
      />
      <section className="py-24 md:py-32">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 lg:px-14">
          <Reveal>
            <p className="eyebrow">{t.pages.privacy.title}</p>
            <h2 className="heading-display mt-3 text-3xl sm:text-4xl uppercase text-black">
              {isDe ? (
                <>Datenschutz<span className="text-[#C20E1A]">erklärung</span></>
              ) : (
                <>Data protection <span className="text-[#C20E1A]">declaration</span></>
              )}
            </h2>
          </Reveal>
          <Reveal delay={60}>{isDe ? <PrivacyDE /> : <PrivacyEN />}</Reveal>
        </div>
      </section>
    </div>
  );
}
